import { InventoryWebsiteVisibilityComponent } from '../../../inventory-website-visibility.component';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

interface SlackAllocation {
  bagsAffected: number;

  fullBags: number;
  weightPerBag: number;
  allocatedWeight: number;

  slackBags: number;
  leftoverWeight: number;

  resultingBags: number;
  slackAdjustment: number;

  mode: 'automatic' | 'manual';
}

@Component({
  selector: 'app-create-slack',
  standalone: true,
  imports: [InventoryWebsiteVisibilityComponent,
    CommonModule,
    RmsModule,
    FormsModule
  ],
  templateUrl: './create-slack.component.html',
  styleUrl: './create-slack.component.scss'
})
export class CreateSlackComponent {
  WebInvisible = true;

  InventoryItem: any;
  ManualWeight: boolean = false;
  TotalSlackWeight: number | null = null;
  BagsAffected: number | null = null;
  MinBagWeight: number = 50;
  MaxBagWeight: number = 150;

  errorMessage: string | null = null;

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: any,
    public ref: RmsPulldownRef
  ) {
    this.InventoryItem = data || {};
  }

  get availableWeight(): number {
    return (this.InventoryItem?.AvailableQty ?? 0) *
      (this.InventoryItem?.NetWeight ?? 0);
  }

  get allocation(): SlackAllocation | null {

    const totalWeight = Number(this.TotalSlackWeight);
    const bagsAffected = Number(this.BagsAffected);

    if (
      !totalWeight ||
      totalWeight <= 0 ||
      !bagsAffected ||
      bagsAffected <= 0
    ) {
      return null;
    }

    /*
     * AUTOMATIC MODE
     *
     * Uses the original inventory NetWeight.
     *
     * Example:
     * BagsAffected = 5
     * NetWeight = 132
     * TotalSlackWeight = 400
     *
     * 3 full bags @ 132 = 396
     * 1 slack bag @ 4
     *
     * Resulting bags = 4
     * Slack adjustment = 1
     */
    if (!this.ManualWeight) {

      const originalNetWeight =
        Number(this.InventoryItem?.NetWeight ?? 0);

      if (originalNetWeight <= 0) {
        return null;
      }

      const fullBags =
        Math.floor(totalWeight / originalNetWeight);

      const allocatedWeight =
        fullBags * originalNetWeight;

      const leftoverWeight =
        this.round(totalWeight - allocatedWeight);

      const slackBags =
        leftoverWeight > 0 ? 1 : 0;

      const resultingBags =
        fullBags + slackBags;

      const slackAdjustment =
        bagsAffected - resultingBags;

      return {
        mode: 'automatic',

        bagsAffected,

        fullBags,
        weightPerBag: this.round(originalNetWeight),
        allocatedWeight: this.round(allocatedWeight),

        slackBags,
        leftoverWeight,

        resultingBags,
        slackAdjustment
      };
    }

    /*
     * MANUAL MODE
     */
    if (
      !this.MinBagWeight ||
      !this.MaxBagWeight ||
      this.MinBagWeight <= 0 ||
      this.MaxBagWeight <= 0 ||
      this.MinBagWeight > this.MaxBagWeight
    ) {
      return null;
    }

    /*
     * Determine how many bags are required to keep
     * each resulting bag <= MaxBagWeight.
     */
    const bagCount =
      Math.ceil(totalWeight / this.MaxBagWeight);

    if (bagCount <= 0) {
      return null;
    }

    const evenWeight =
      totalWeight / bagCount;

    /*
     * Entire weight fits cleanly inside the selected
     * Min / Max range.
     */
    if (evenWeight >= this.MinBagWeight) {

      const resultingBags = bagCount;

      return {
        mode: 'manual',

        bagsAffected,

        fullBags: bagCount,
        weightPerBag: this.round(evenWeight),
        allocatedWeight: this.round(totalWeight),

        slackBags: 0,
        leftoverWeight: 0,

        resultingBags,

        slackAdjustment:
          bagsAffected - resultingBags
      };
    }

    /*
     * Weight cannot be evenly distributed while
     * satisfying the minimum bag weight.
     *
     * Create as many minimum-weight bags as possible
     * and put the remainder into one Slack bag.
     */
    const fullBags =
      Math.floor(totalWeight / this.MinBagWeight);

    const allocatedWeight =
      fullBags * this.MinBagWeight;

    const leftoverWeight =
      this.round(totalWeight - allocatedWeight);

    const slackBags =
      leftoverWeight > 0 ? 1 : 0;

    const resultingBags =
      fullBags + slackBags;

    const slackAdjustment =
      bagsAffected - resultingBags;

    return {
      mode: 'manual',

      bagsAffected,

      fullBags,
      weightPerBag: this.round(this.MinBagWeight),
      allocatedWeight: this.round(allocatedWeight),

      slackBags,
      leftoverWeight,

      resultingBags,
      slackAdjustment
    };
  }
  get canSave(): boolean {

    const allocation = this.allocation;

    if (!allocation) {
      return false;
    }

    if (
      !this.TotalSlackWeight ||
      this.TotalSlackWeight <= 0
    ) {
      return false;
    }

    if (
      !this.BagsAffected ||
      this.BagsAffected <= 0
    ) {
      return false;
    }

    return this.validationMessage === null;
  }
  save(): void {

    this.errorMessage = null;

    if (
      !this.TotalSlackWeight ||
      this.TotalSlackWeight <= 0
    ) {
      this.errorMessage =
        'Enter a valid total weight to process.';
      return;
    }

    if (this.TotalSlackWeight > this.availableWeight) {
      this.errorMessage =
        `Only ${this.availableWeight} lbs available on this ref#.`;
      return;
    }

    if (
      this.ManualWeight &&
      this.MinBagWeight > this.MaxBagWeight
    ) {
      this.errorMessage =
        'Minimum bag weight cannot exceed maximum bag weight.';
      return;
    }

    const allocation = this.allocation;

    if (!allocation) {
      this.errorMessage =
        'Unable to calculate the inventory allocation.';
      return;
    }
    if (
      !this.BagsAffected ||
      this.BagsAffected <= 0
    ) {
      this.errorMessage =
        'Enter the number of bags affected.';
      return;
    }

    const resultingBags =
      allocation.fullBags +
      (allocation.leftoverWeight > 0 ? 1 : 0);

    if (this.BagsAffected < resultingBags) {
      this.errorMessage =
        `This allocation creates ${resultingBags} bags, but only ${this.BagsAffected} bags were marked as affected.`;
      return;
    }
    this.ref.close({
      cancelled: false,
      WebInvisible: this.WebInvisible,

      mode: allocation.mode,

      TotalSlackWeight:
        this.TotalSlackWeight,

      BagsAffected:
        allocation.bagsAffected,

      FullBagsToCreate:
        allocation.fullBags,

      FullBagWeight:
        allocation.weightPerBag,

      AllocatedWeight:
        allocation.allocatedWeight,

      SlackBagsToCreate:
        allocation.slackBags,

      SlackWeight:
        allocation.leftoverWeight,

      SlackAdjustment:
        allocation.slackAdjustment,

      MinBagWeight:
        this.ManualWeight
          ? this.MinBagWeight
          : null,

      MaxBagWeight:
        this.ManualWeight
          ? this.MaxBagWeight
          : null
    });
  }
  get slackBagsToCreate(): number {
    return this.allocation?.slackBags ?? 0;
  }
  get slackAdjustment(): number {
    return Math.max(
      0,
      this.allocation?.slackAdjustment ?? 0
    );
  }
  cancel(): void {
    this.ref.close({
      cancelled: true
    });
  }
  get validationMessage(): string | null {

    const totalWeight =
      Number(this.TotalSlackWeight ?? 0);

    const bagsAffected =
      Number(this.BagsAffected ?? 0);

    /*
     * Don't show errors before the user
     * has entered anything.
     */
    if (totalWeight <= 0 && bagsAffected <= 0) {
      return null;
    }

    /*
     * Total requested weight cannot exceed
     * the inventory's available weight.
     */
    if (totalWeight > this.availableWeight) {
      return `This requires ${this.round(totalWeight)} lbs, but only ${this.round(this.availableWeight)} lbs are available on this ref#.`;
    }

    /*
     * Bags affected cannot exceed the number
     * of bags actually available.
     */
    const availableQty =
      Number(this.InventoryItem?.AvailableQty ?? 0);

    if (bagsAffected > availableQty) {
      return `This requires ${bagsAffected} bag(s), but only ${availableQty} bag(s) are available on this ref#.`;
    }

    /*
     * Manual weight range validation.
     */
    if (
      this.ManualWeight &&
      this.MinBagWeight > this.MaxBagWeight
    ) {
      return 'Minimum bag weight cannot exceed maximum bag weight.';
    }

    const allocation = this.allocation;

    if (!allocation) {
      return null;
    }

    /*
     * Result cannot contain more bags than
     * the number of source bags affected.
     */
    if (allocation.slackAdjustment < 0) {
      return `This allocation creates ${allocation.resultingBags} bag(s), but only ${bagsAffected} bag(s) were marked as affected.`;
    }

    return null;
  }
  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
